import { useState } from "react";
import {
  useCanAccess,
  useCreate,
  useGetIdentity,
  useGetList,
  useNotify,
  useRedirect,
} from "ra-core";
import { Button } from "@/components/ui/button";
import { FolderKanban } from "lucide-react";

import type { Deal } from "../types";

type Project = { id: number; name: string; lead_id: number | null };

/**
 * Turns a won lead into a project.
 *
 * The lead is copied forward, not consumed: it stays in the pipeline with its
 * history, and the new project records where it came from in lead_id. That was
 * an explicit requirement -- converting must not be a status change that makes
 * the lead disappear from the funnel it was won in.
 *
 * Converting twice is prevented by looking for an existing project on this
 * lead, so a second click opens the project rather than creating a duplicate.
 */
export function ConvertToProjectButton({ record }: { record: Deal }) {
  const { identity } = useGetIdentity();
  const notify = useNotify();
  const redirect = useRedirect();
  const [busy, setBusy] = useState(false);

  const canCreate = useCanAccess({ resource: "projects", action: "create" });

  const { data: existing } = useGetList<Project>(
    "projects",
    { pagination: { page: 1, perPage: 1 }, filter: { lead_id: record?.id } },
    { enabled: record?.id != null },
  );

  const [create] = useCreate();

  if (!record || !canCreate.canAccess) return null;

  const alreadyConverted = (existing?.length ?? 0) > 0;

  const convert = () => {
    if (alreadyConverted) {
      redirect("show", "projects", existing![0].id);
      return;
    }

    setBusy(true);
    create(
      "projects",
      {
        data: {
          name: record.name,
          description: record.description,
          lead_id: record.id,
          company_id: record.company_id,
          // A deal carries contact_ids; the first is treated as the client
          // until someone says otherwise on the project itself.
          client_contact_id: record.contact_ids?.[0] ?? null,
          sales_id: identity?.id,
          status: "active",
          stage: "discovery",
        },
      },
      {
        onSuccess: (project: { id: number }) => {
          setBusy(false);
          notify(
            "Project created from this lead. The lead stays in the pipeline.",
            {
              type: "info",
            },
          );
          redirect("show", "projects", project.id);
        },
        onError: (error: unknown) => {
          setBusy(false);
          notify(
            error instanceof Error
              ? error.message
              : "Could not create the project",
            { type: "error" },
          );
        },
      },
    );
  };

  return (
    <Button variant="outline" onClick={convert} disabled={busy}>
      <FolderKanban className="size-4" />
      {alreadyConverted ? "Open project" : "Convert to project"}
    </Button>
  );
}
